/** @jsx jsx */
import React, { useState, createContext, useEffect } from "react";
import { css, jsx } from "@emotion/core";
import BlockUI from "components/molecules/BlockUi";
import FlexBox from "components/atoms/FlexBox";
import moment from "moment";
import useLoginUser from "hooks/useLoginUser";
import FormLabel from "components/atoms/Form/FormLabel";
import FormContents from "components/atoms/Form/FormContents";
import { grayScale } from "components/styles";
import TabControl from "components/atoms/TabControl";
import BarcodeRecorder from "./BarcodeRecorder";
import FaceRecorder from "./FaceRecorder";
import GPSRecorder from "./GPSRecorder";
import DataTable from "components/organismos/DataTable/DataTable";
import useRecorder from "./hook";
import { Column } from "react-table";
import { utilityColor } from "components/styles";
import ReactTooltip from "react-tooltip";
import Select from "components/atoms/Select";
import { useOrgTreesOptionsAll } from "hooks/useOrgTreesOptions";
import BodyText from "components/atoms/BodyText";
import {
  useOrgCode,
  useOrgName,
  getBarcodeStamp,
  getFaceStamp,
  getCompanyCode,
  getStaffCode,
} from "hooks/useSessionLoginUser";
import { CurrentTimeDomain } from "domain/master/attend/currentTime";
import ImageModel from "../ImageModel";
import { useParams } from "react-router-dom";
import TextTooltip from "components/molecules/TextTooltip";
import FormHeader from 'components/templates/FormHeader';
import SelectForm from "components/molecules/SelectForm";
import FlexBoxItem from "components/atoms/FlexBoxItem";

type SelectType = "id" | "face" | "gps";

const SelectType: { [key in SelectType]: SelectType } = {
	id: "id",
	face: "face",
	gps: "gps"
};

const styles = {
	backHistory: css(
		{
			position: "relative",
			top: "55px",
			marginTop: "-40px",
			marginRight: "20px"
			// left: "-65px"
		},
		css`
			&:hover svg {
				opacity: 0.7;
			}
		`
	),

	labelForm: css({
		margin: "15px 0 20px 0",
		color: grayScale.gray01,
		fontSize: "18px"
	})
};

const dataTable = css`
	table{
		table-layout: auto;
	}
	margin: 24px 0 16px 0;
	table th:nth-of-type(7){
		width: 100px;	
	}
	table td:nth-of-type(7){
		text-align: center;
	}
`;

//Context global variable
type contextType = {
	companyCode: string;
	stampByBelongOrg: number;
	stampOrgCode: string;
	stampOrgName: string;
	loginOrgCode: string;
	loginOrgName: string;
	useFaceStamp: number;
	useBarcodeStamp: number;
	barcodeId: string;
	staffName: string;
	staffCode: string;
	resultCurrentTime: CurrentTimeDomain;
	remote: boolean;
	setRemote: any;
};

export const TimeRecorderContext = createContext<contextType>(
  {} as contextType
);

const Recorder_v1: React.FC = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [remote, setRemote] = useState(false);
	
	let { id } = useParams<Record<string, string | undefined>>();
	
	const { staffName } = useLoginUser();

	// const showIdStamp = sessionStorage.getItem("loginUser.useInsideLoginBarcodeStamp") === "true" ? true : false;
	// const showFaceStamp = sessionStorage.getItem("loginUser.useInsideLoginFaceRecorderStamp") === "true" ? true : false;
	// const showGpsStamp = sessionStorage.getItem("loginUser.useGpsStamp") === "true" ? true : false;
	
	const [selectType, setSelectType] = React.useState<string>('');

	useEffect(() => {
		setSelectType(id);
	}, [id, selectType]);

	const orgTreesOptions = useOrgTreesOptionsAll(undefined, undefined, false, false);

	const columns: Array<Column<any>> = React.useMemo(() => [
		{
			Header: () => "氏名",
			accessor: "staffName",
			id: "staffName",
			Cell: (cell: { row: { isExpanded: boolean; original: any }; page: [] }) => {
				const [imageOpen, setImageOpen] = useState(false);
				const { staffCode, staffName, stampTime, stampName } = cell.row.original;
				const imageURL = cell.row.original.photoFullpath;

				return (
					<FlexBox>
						<div>
							{
								imageURL && (
									<React.Fragment>
										<div 
											style={{
												textAlign: "center", 
												display: "flex", 
												alignItems: "center"
											}}
										>
											<img
												src={imageURL}
												alt={imageURL}
												width={55}
												height={55}
												style={{
													cursor: "pointer", 
													borderRadius: "50%", 
													marginRight: "15px"
												}}
												onClick={() => setImageOpen(true)}
											/>
											<ImageModel
												open={imageOpen}
												closeHandler={() => {
													setImageOpen(false);
												}}
												imageURL={imageURL}
												descript={
													staffCode + "/" +
													staffName + "/" +
													stampName + "/" +
													moment(stampTime).format("YYYY/MM/DD HH:mm")
												}
											/>
										</div>
									</React.Fragment>
								)
							}
						</div>
							{
								staffName.length >= 20 ? (
									<TextTooltip
										text={staffName}
										textDis={
											<div
												className="ellipsis headline-cell-width-120"
												style={{
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													overflow: "hidden",
													width: `${imageURL !== null ? "70px" : "140px"}`
												}}>
												<BodyText>
													{cell.row.original.staffName}
												</BodyText>
											</div>
										}
									/>
								) : (
									<BodyText>
										{cell.row.original.staffName}
									</BodyText>
								)
							}
					</FlexBox>
				);
			}
		},
		{
			Header: "組織コード",
			accessor: "orgCode"
		},
		{
			Header: "組織名",
			accessor: "orgName"
		},
		{
			Header: "スタッフコード",
			accessor: "staffCode"
		},
		{
			Header: "打刻方法",
			accessor: "stampModeName",
			id: "stampModeName",
			Cell: (cell: { row: { isExpanded: boolean; original: any }; page: [] }) => {
				return (
					<React.Fragment>
						<div style={{ width: "100%" }}>
							{ cell.row.original.stampModeName }
						</div>
						{
							cell.row.original.errorMessage !== null && cell.row.original.errorMessage !== "" && (
								<BodyText customStyle={css({ fontSize: "13px", color: "#333333" })}>
									{cell.row.original.cardId}
								</BodyText>
							)
						}
					</React.Fragment>
				);
			}
		},
		{
			Header: () => "打刻種別",
			accessor: "errorMessage",
			id: "errorMessage",
			Cell: (cell: { row: { isExpanded: boolean; original: any }; page: [] }) => {
				const { stampName, isRemote } = cell.row.original;
				if(cell.row.original.errorMessage === null || cell.row.original.errorMessage === "") {
					return (
						<div>
							{
								stampName === "出勤"
								? (
										isRemote
										? stampName + " (テレワーク)"
										: stampName + " (オフィス)"
									)
								: stampName
							}
						</div>
					);
				}
				if(cell.row.original.errorMessage !== null && cell.row.original.errorMessage !== "") {
					return (
						<span>
							<div css={css({ color: utilityColor.note })} 
								   data-tip={cell.row.original.errorMessage}
							>
								{stampName}
							</div>
							<ReactTooltip html={true} />
						</span>
					);
				}
			}
		},
		{
			Header: () => "打刻時刻",
			accessor: "stampTime",
			id: "stampTime",
			Cell: (cell: { row: { isExpanded: boolean; original: any }; page: [] }) =>
				cell.row.original.stampTime 
				? moment(cell.row.original.stampTime).format("YYYY/MM/DD HH:mm:ss") 
				: ""
		}
	], []);

	const {
		stampHistoryList,
		currentDay,
		timeNow,
		second,
		resultCurrentTime,
		userRole,
		selectedOrg,
		setSelectedOrg,
		fetchHistoryData
	} = useRecorder(setIsLoading);

	 const dataTimes = {
		timeNow,
		currentDay,
		second,
		resultCurrentTime
	};

	return (
		<TimeRecorderContext.Provider
			value={{
				companyCode: getCompanyCode(),
				stampByBelongOrg: 0,
				stampOrgCode: useOrgCode(),
				stampOrgName: useOrgName(),
				loginOrgCode: useOrgCode(),
				loginOrgName: useOrgName(),
				useFaceStamp: getFaceStamp(),
				useBarcodeStamp: getBarcodeStamp(),
				barcodeId: "",
				staffCode: getStaffCode(),
				staffName,
				resultCurrentTime,
				remote,
				setRemote
			}}>
			<BlockUI blocking={isLoading}>
				<FormHeader title="打刻"/>
					<FormContents 
						customStyle={
							css({
								display: 'flex', 
								flexDirection: 'column', 
								flexWrap: 'wrap', 
								marginTop: '60px',
								marginBottom: '16px',
							})
						}
					>
						{
							selectType === SelectType.id && (
								<BarcodeRecorder  
									dataTimes={dataTimes} 
									setIsLoading={setIsLoading} 
									fetchHistoryData={fetchHistoryData} 
								/>
							)
						}
						{
							selectType === SelectType.face && (
								<FaceRecorder 
									dataTimes={dataTimes} 
									setIsLoading={setIsLoading} 
									fetchHistoryData={fetchHistoryData} 
								/>
							)
						}
						{
							selectType === SelectType.gps && (
								<GPSRecorder 
									dataTimes={dataTimes} 
									setIsLoading={setIsLoading} 
									fetchHistoryData={fetchHistoryData} 
								/>
							)
						}
					</FormContents>
					{
						userRole !== "1" && (
							<div css={dataTable}>
								<FormContents>
									<div css={css({ marginLeft: "15px" })}>
										<FormLabel 
											label="打刻履歴" 
											customStyle={css({ fontWeight: "bold", marginTop: "20px" })} 
										/>
										{
											userRole === "3" && (
												<div css={css({ marginTop: "10px" })}>
													<FlexBoxItem width="580px"> 
														<SelectForm
															name="orgState"
															value={String(selectedOrg)}
															setValue={(arg: string) => {
																setSelectedOrg(arg);
															}}
															options={orgTreesOptions}
														/>
													</FlexBoxItem>
												</div>
											)
										}
									</div>
									<div 
										className='table-bordered table-padding' 
										style={{ overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '15px' }}
									>
										<DataTable
											columns={
												remote
												? columns
												: columns.filter(item => {
														return item.accessor !== "isRemote";
												})
											}
											classNameParrant="border-left-right"
											className="table-no-border-left-right"
											data={stampHistoryList}
											isGlobalFilter={true}
											minWidth="100%"
										/>
									</div>							
								</FormContents>
							</div>
						)
					}
			</BlockUI>
		</TimeRecorderContext.Provider>
	);
};

export default Recorder_v1;
